import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isNumberEmpty, isNumberNotEmpty, isObjectEmpty, isObjectNotEmpty, isObjectNotEmptyWithoutEmptyParams, isStringEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import logger from "../../../utils/logger";
import { getTestModeConsole } from "../../../utils/testMode";
import SiteVisit from "../../../models/SiteVisit";
import SiteEvent from "../../../models/SiteEvent";
import { getClientIPAddress, getClientUserAgent, getReferrerUrl } from "../../../utils/clientUtils";
import { validateEmail, validatePhone } from "../../../firebase/IPQS";
import DeviceFingerprint from 'node_js_ipqs_device_tracker';
import AffiliateInfo from "../../../models/AffiliateInfo";
import Lead from "../../../models/Lead";
import { getDefaultCampaign, getProductCategoryBySlug } from "../../../utils/entityUtils";
import { useMemo } from "react";
import { getCampaigns, getProductCategories, getProducts, getPrograms, getOfferGroups, selectDefaultCampaignByProgramSlug, selectDefaultProductByProgramSlug, selectProductCategoryBySlug, selectProgramBySlug } from "../orchestrator";
import { toLower } from "lodash";
import { getEffectiveOfferGroupId, getEffectiveOfferToken, getOfferGroupById, resolveOfferContext } from "../../../utils/productIdentityUtils";
import { saveFunnelProgress } from "../../../firebase/Leads";

const initialState = {
	siteVisitId: null,
	siteVisit: null,
	siteVisitAt: null,

	deviceInfo: null,
	emailInfo: null,
	phoneInfo: null,
	affiliateInfo: {},
	ipAddress: null,
	referrerUrl: null,
	userAgent: null,
	deviceFingerprint: null,

	campaignSlug: null,
	campaignId: null,
	campaign: null,

	programSlug: null,
	programId: null,
	program: null,

	productSku: null,
	productId: null,
	product: null,
	offerGroupId: null,
	offerToken: null,

	productCategorySlug: null,
	productCategoryId: null,
	productCategory: null,

	affiliateId: null,
	subAffiliateId: null,

	creativeId: null,
	version: null,
	clickId: null,
	source: null,
	couponCode: null,

	testMode: false,

	fbclid: null,
	fbc: null,
	ttclid: null,
	gclid: null,
	irclickid: null,
	rdt_cid: null,
	SsCid: null,
	msclkid: null,
	twclid: null,
	nbclid: null,
	gbraid: null,

	addon: false,

	leadId: null,
	lead: null,
	userId: null,

	theOfferSession: null,
	initialPath: window?.location?.pathname,

	offerId: null,
	affId: null,
	sub1: null,
	sub2: null,
	sub3: null,
	sub4: null,
	sub5: null,
	uId: null,
	sourceId: null,
	transactionId: null,
	sessionId: null,

	isLoading: false,
	error: null,

	utm_source: null,
	utm_medium: null,
	utm_campaign: null,
	utm_term: null,
	utm_content: null
};

const shouldResolveProductContext = (updatedFields = {}) => {
	return ['programSlug', 'productSku', 'offerGroupId', 'offerToken'].some((fieldName) => updatedFields[fieldName] !== undefined);
};

const resolveAnalyticsIdentityFields = async ({ dispatch, analyticsState, updatedFields }) => {
	const resolvedUpdatedFields = isObjectNotEmpty(updatedFields) ? { ...updatedFields } : {};
	if (!shouldResolveProductContext(resolvedUpdatedFields)) {
		return resolvedUpdatedFields;
	}

	const programs = await dispatch(getPrograms(false));
	const products = await dispatch(getProducts(false));
	const offerGroups = await dispatch(getOfferGroups(false));
	if (!isListNotEmpty(programs) || !isListNotEmpty(products)) {
		return resolvedUpdatedFields;
	}

	const explicitOfferGroup = isStringNotEmpty(resolvedUpdatedFields.offerGroupId)
		? getOfferGroupById(resolvedUpdatedFields.offerGroupId, offerGroups)
		: null;
	const requestedProgramSlug = isStringNotEmpty(resolvedUpdatedFields.programSlug) ? resolvedUpdatedFields.programSlug : analyticsState?.programSlug;
	const requestedProductValue = resolvedUpdatedFields.offerToken !== undefined
		? resolvedUpdatedFields.offerToken
		: (resolvedUpdatedFields.productSku !== undefined ? resolvedUpdatedFields.productSku : (explicitOfferGroup?.offerToken ?? analyticsState?.offerToken ?? analyticsState?.productSku ?? null));

	const resolvedContext = resolveOfferContext({
		requestedProgramSlug,
		requestedProductValue,
		products,
		programs,
		offerGroups
	});

	if (isObjectNotEmpty(resolvedContext.program)) {
		resolvedUpdatedFields.programSlug = resolvedContext.program.slug;
		resolvedUpdatedFields.program = resolvedContext.program;
		resolvedUpdatedFields.programId = resolvedContext.program.id;
	}

	if (isObjectNotEmpty(resolvedContext.product)) {
		resolvedUpdatedFields.productSku = resolvedContext.product.sku;
		resolvedUpdatedFields.product = resolvedContext.product;
		resolvedUpdatedFields.productId = resolvedContext.product.id;
		resolvedUpdatedFields.offerGroupId = resolvedContext.offerGroupId ?? getEffectiveOfferGroupId(resolvedContext.product);
		resolvedUpdatedFields.offerToken = resolvedContext.offerToken ?? getEffectiveOfferToken(resolvedContext.product, offerGroups);
		return resolvedUpdatedFields;
	}

	if (isStringNotEmpty(resolvedUpdatedFields.productSku) && resolvedUpdatedFields.productSku === requestedProductValue) {
		const exactProduct = products.find((product) => product.sku === resolvedUpdatedFields.productSku) ?? null;
		if (isObjectNotEmpty(exactProduct)) {
			resolvedUpdatedFields.product = exactProduct;
			resolvedUpdatedFields.productId = exactProduct.id;
			resolvedUpdatedFields.offerGroupId = getEffectiveOfferGroupId(exactProduct);
			resolvedUpdatedFields.offerToken = getEffectiveOfferToken(exactProduct, offerGroups);
			return resolvedUpdatedFields;
		}
	}

	if (isStringNotEmpty(resolvedContext.offerGroupId)) {
		resolvedUpdatedFields.offerGroupId = resolvedContext.offerGroupId;
	}
	if (isStringNotEmpty(resolvedContext.offerToken)) {
		resolvedUpdatedFields.offerToken = resolvedContext.offerToken;
	}

	return resolvedUpdatedFields;
};

const analyticsReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {
		case 'UPDATE_ANALYTICS':
			// console.log('updating analytics', payload);
			return {
				...state,
				...payload
			};
		case 'UPDATE_ANALYTICS_LEAD':
			// console.log('updating analytics lead', payload);
			return {
				...state,
				lead: payload
			};
		case 'UPDATE_ANALYTICS_LEADID':
			// console.log('updating analytics leadId', payload);
			return {
				...state,
				leadId: payload
			};
		case 'UPDATE_ANALYTICS_SITEVISIT':
			// console.log('updating analytics siteVisit', payload);
			return {
				...state,
				siteVisit: payload
			};
		case 'UPDATE_ANALYTICS_SITEVISITID':
			// console.log('updating analytics siteVisitId', payload);
			return {
				...state,
				siteVisitId: payload
			};
		case 'UPDATE_ANALYTICS_SITEVISITAT':
			// console.log('updating analytics siteVisitAt', payload);
			return {
				...state,
				siteVisitAt: payload
			};
		case 'UPDATE_ANALYTICS_FBC':
			// console.log('updating analytics fbc', payload);
			return {
				...state,
				fbc: payload
			};
		case 'UPDATE_ANALYTICS_DEVICEFINGERPRINT':
			// console.log('updating analytics deviceFingerprint', payload);
			return {
				...state,
				deviceFingerprint: payload
			};
		case 'UPDATE_ANALYTICS_IPADDRESS':
			// console.log('updating analytics ipAddress', payload);
			return {
				...state,
				ipAddress: payload
			};
		case 'UPDATE_ANALYTICS_DEVICEINFO':
			// console.log('updating analytics deviceInfo', payload);
			return {
				...state,
				deviceInfo: payload
			};
		case 'UPDATE_ANALYTICS_EMAILINFO':
			// console.log('updating analytics emailInfo', payload);
			return {
				...state,
				emailInfo: payload
			};
		case 'UPDATE_ANALYTICS_PHONEINFO':
			// console.log('updating analytics phoneInfo', payload);
			return {
				...state,
				phoneInfo: payload
			};
		case 'UPDATE_ANALYTICS_AFFILIATEINFO':
			// console.log('updating analytics affiliateInfo', payload);
			return {
				...state,
				affiliateInfo: payload
			};
		case 'UPDATE_ANALYTICS_INITIALPATH':
			// console.log('updating analytics initialPath', payload);
			return {
				...state,
				initialPath: payload
			};
		case 'UPDATE_ANALYTICS_THEOFFERSESSION':
			// console.log('updating analytics theOfferSession', payload);
			return {
				...state,
				theOfferSession: payload,
				sessionId: payload,
				transactionId: payload
			};
		case 'RESET_ANALYTICS':
			return {
				...state,
				...initialState
			};
		default:
			return state;
	}
};

export const getSiteVisit = (siteVisitId) => {
	return async (dispatch) => {
		try {
			if (isStringNotEmpty(siteVisitId)) {
				const match = await SiteVisit.getById(siteVisitId);
				// console.log('match', match);

				let updatedStateValues = {
					siteVisit: match,
					siteVisitId: match?.id ?? null
				};

				await dispatch({ type: 'UPDATE_ANALYTICS', payload: { ...updatedStateValues } });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateAnalytics = (updatedFields) => {
	return async (dispatch, getState) => {
		try {
			updatedFields = isObjectNotEmpty(updatedFields) ? { ...updatedFields } : {};
			const state = getState();
			const analyticsState = { ...state.analyticsState };

			let siteVisit = analyticsState?.siteVisit;
			let lead = analyticsState?.lead;
			let affiliateInfo = analyticsState?.affiliateInfo;

			if (isObjectNotEmpty(updatedFields?.affiliateInfo)) {
				affiliateInfo = { ...updatedFields?.affiliateInfo };
			}

			let updatedSiteVisitFields = {};
			let updatedLeadFields = {};

			updatedFields = await resolveAnalyticsIdentityFields({ dispatch, analyticsState, updatedFields });
			if (updatedFields['productCategorySlug'] !== undefined) {
				const productCategories = await dispatch(getProductCategories(false));
				if (isListNotEmpty(productCategories)) {
					let productCategorySlug = updatedFields['productCategorySlug'];

					const productCategory = getProductCategoryBySlug(productCategorySlug, productCategories);
					const productCategoryId = productCategory?.id ?? 'none';

					updatedFields.productCategory = productCategory;
					updatedFields.productCategoryId = productCategoryId;
				}
			}
			if (updatedFields['campaignSlug'] !== undefined) {
				const programs = await dispatch(getPrograms(false));
				const campaigns = await dispatch(getCampaigns(false));
				if (isListNotEmpty(campaigns) && isListNotEmpty(programs)) {
					let campaignSlug = updatedFields['campaignSlug'];
					let programSlug = isStringNotEmpty(updatedFields['programSlug']) ? updatedFields['programSlug'] : analyticsState?.programSlug;

					const campaign = getDefaultCampaign(programSlug, campaignSlug, campaigns, programs);
					const campaignId = campaign?.id ?? 'none';

					updatedFields.campaign = campaign;
					updatedFields.campaignId = campaignId;
				}
			}

			// happens when setLead is called
			if (isObjectNotEmpty(updatedFields?.lead)) {
				let updatedLead = { ...updatedFields?.lead };
				if (isStringEmpty(updatedFields?.offerGroupId) && isStringNotEmpty(updatedLead?.offerGroupId)) {
					updatedFields.offerGroupId = updatedLead.offerGroupId;
				}
				if (isStringEmpty(updatedFields?.offerToken) && isStringNotEmpty(updatedLead?.offerToken)) {
					updatedFields.offerToken = updatedLead.offerToken;
				}

				// console.log('lead', updatedLead, updatedLead?.affiliateInfo, affiliateInfo);
				// only seed a lead missing attribution - once a lead has affiliateInfo, only a checkout attempt may re-attribute it
				if (isObjectNotEmpty(affiliateInfo) && isStringNotEmpty(affiliateInfo?.affiliateId) && toLower(affiliateInfo?.affiliateId) !== 'none' && isObjectNotEmpty(updatedLead) && isStringNotEmpty(updatedLead?.id) && isObjectEmpty(updatedLead?.affiliateInfo)) {
					// update lead with new affiliate attribution
					updatedLeadFields = {
						affiliateInfo: affiliateInfo,
						affiliateId: affiliateInfo?.affiliateId,
						subAffiliateId: affiliateInfo?.subAffiliateId
					};
				} else if (isObjectNotEmpty(affiliateInfo) && isStringNotEmpty(affiliateInfo?.affiliateId) && (affiliateInfo?.affiliateId === 'none' || affiliateInfo?.affiliateId === null) && isObjectNotEmpty(updatedLead) && isStringNotEmpty(updatedLead?.affiliateId) && updatedLead?.affiliateId !== 'none' && isObjectNotEmpty(updatedLead?.affiliateInfo) && isStringNotEmpty(updatedLead?.affiliateInfo?.affiliateId) && updatedLead?.affiliateInfo?.affiliateId !== 'none') {
					// load the lead affiliateInfo into the current analytics session
					let updatedAffiliateInfo = new AffiliateInfo({ ...affiliateInfo }).toDto();

					if (isStringEmpty(updatedAffiliateInfo?.offerId) && isStringNotEmpty(updatedLead?.affiliateInfo?.offerId)) {
						updatedAffiliateInfo.offerId = updatedLead?.affiliateInfo?.offerId;
						updatedSiteVisitFields.offerId = updatedLead?.affiliateInfo?.offerId;
					}
					if (isStringEmpty(updatedAffiliateInfo?.affId) && isStringNotEmpty(updatedLead?.affiliateInfo?.affId)) {
						updatedAffiliateInfo.affId = updatedLead?.affiliateInfo?.affId;
						updatedSiteVisitFields.affId = updatedLead?.affiliateInfo?.affId;
					}
					if (isStringEmpty(updatedAffiliateInfo?.sub1) && isStringNotEmpty(updatedLead?.affiliateInfo?.sub1)) {
						updatedAffiliateInfo.sub1 = updatedLead?.affiliateInfo?.sub1;
						updatedSiteVisitFields.sub1 = updatedLead?.affiliateInfo?.sub1;
					}
					if (isStringEmpty(updatedAffiliateInfo?.sub2) && isStringNotEmpty(updatedLead?.affiliateInfo?.sub2)) {
						updatedAffiliateInfo.sub2 = updatedLead?.affiliateInfo?.sub2;
						updatedSiteVisitFields.sub2 = updatedLead?.affiliateInfo?.sub2;
					}
					if (isStringEmpty(updatedAffiliateInfo?.sub3) && isStringNotEmpty(updatedLead?.affiliateInfo?.sub3)) {
						updatedAffiliateInfo.sub3 = updatedLead?.affiliateInfo?.sub3;
						updatedSiteVisitFields.sub3 = updatedLead?.affiliateInfo?.sub3;
					}
					if (isStringEmpty(updatedAffiliateInfo?.sub4) && isStringNotEmpty(updatedLead?.affiliateInfo?.sub4)) {
						updatedAffiliateInfo.sub4 = updatedLead?.affiliateInfo?.sub4;
						updatedSiteVisitFields.sub4 = updatedLead?.affiliateInfo?.sub4;
					}
					if (isStringEmpty(updatedAffiliateInfo?.sub5) && isStringNotEmpty(updatedLead?.affiliateInfo?.sub5)) {
						updatedAffiliateInfo.sub5 = updatedLead?.affiliateInfo?.sub5;
						updatedSiteVisitFields.sub5 = updatedLead?.affiliateInfo?.sub5;
					}
					if (isStringEmpty(updatedAffiliateInfo?.uId) && isStringNotEmpty(updatedLead?.affiliateInfo?.uId)) {
						updatedAffiliateInfo.uId = updatedLead?.affiliateInfo?.uId;
						updatedSiteVisitFields.uId = updatedLead?.affiliateInfo?.uId;
					}
					if (isStringEmpty(updatedAffiliateInfo?.sourceId) && isStringNotEmpty(updatedLead?.affiliateInfo?.sourceId)) {
						updatedAffiliateInfo.sourceId = updatedLead?.affiliateInfo?.sourceId;
						updatedSiteVisitFields.sourceId = updatedLead?.affiliateInfo?.sourceId;
					}
					if (isStringEmpty(updatedAffiliateInfo?.transactionId) && isStringNotEmpty(updatedLead?.affiliateInfo?.transactionId)) {
						updatedAffiliateInfo.transactionId = updatedLead?.affiliateInfo?.transactionId;
						updatedSiteVisitFields.transactionId = updatedLead?.affiliateInfo?.transactionId;
					}
					if (isStringNotEmpty(updatedLead?.affiliateInfo?.affiliateId) && updatedLead?.affiliateInfo?.affiliateId !== 'none') {
						updatedAffiliateInfo.affiliateId = updatedLead?.affiliateInfo?.affiliateId;
						updatedSiteVisitFields.affiliateId = updatedLead?.affiliateInfo?.affiliateId;
					}
					if (isStringNotEmpty(updatedLead?.affiliateInfo?.subAffiliateId) && updatedLead?.affiliateInfo?.subAffiliateId !== 'none') {
						updatedAffiliateInfo.subAffiliateId = updatedLead?.affiliateInfo?.subAffiliateId;
						updatedSiteVisitFields.subAffiliateId = updatedLead?.affiliateInfo?.subAffiliateId;
					}
					if (isStringEmpty(updatedAffiliateInfo?.clickId) && isStringNotEmpty(updatedLead?.affiliateInfo?.clickId)) {
						updatedAffiliateInfo.clickId = updatedLead?.affiliateInfo?.clickId;
						updatedSiteVisitFields.clickId = updatedLead?.affiliateInfo?.clickId;
					}
					if (isStringEmpty(updatedAffiliateInfo?.fbclid) && isStringNotEmpty(updatedLead?.affiliateInfo?.fbclid)) {
						updatedAffiliateInfo.fbclid = updatedLead?.affiliateInfo?.fbclid;
						updatedSiteVisitFields.fbclid = updatedLead?.affiliateInfo?.fbclid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.fbc) && isStringNotEmpty(updatedLead?.affiliateInfo?.fbc)) {
						updatedAffiliateInfo.fbc = updatedLead?.affiliateInfo?.fbc;
						updatedSiteVisitFields.fbc = updatedLead?.affiliateInfo?.fbc;
						if (isStringEmpty(state?.analyticsState?.fbc)) {
							dispatch(setFbc(updatedLead?.affiliateInfo?.fbc));
						}
					}
					if (isStringEmpty(updatedAffiliateInfo?.ttclid) && isStringNotEmpty(updatedLead?.affiliateInfo?.ttclid)) {
						updatedAffiliateInfo.ttclid = updatedLead?.affiliateInfo?.ttclid;
						updatedSiteVisitFields.ttclid = updatedLead?.affiliateInfo?.ttclid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.gclid) && isStringNotEmpty(updatedLead?.affiliateInfo?.gclid)) {
						updatedAffiliateInfo.gclid = updatedLead?.affiliateInfo?.gclid;
						updatedSiteVisitFields.gclid = updatedLead?.affiliateInfo?.gclid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.irclickid) && isStringNotEmpty(updatedLead?.affiliateInfo?.irclickid)) {
						updatedAffiliateInfo.irclickid = updatedLead?.affiliateInfo?.irclickid;
						updatedSiteVisitFields.irclickid = updatedLead?.affiliateInfo?.irclickid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.rdt_cid) && isStringNotEmpty(updatedLead?.affiliateInfo?.rdt_cid)) {
						updatedAffiliateInfo.rdt_cid = updatedLead?.affiliateInfo?.rdt_cid;
						updatedSiteVisitFields.rdt_cid = updatedLead?.affiliateInfo?.rdt_cid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.SsCid) && isStringNotEmpty(updatedLead?.affiliateInfo?.SsCid)) {
						updatedAffiliateInfo.SsCid = updatedLead?.affiliateInfo?.SsCid;
						updatedSiteVisitFields.SsCid = updatedLead?.affiliateInfo?.SsCid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.msclkid) && isStringNotEmpty(updatedLead?.affiliateInfo?.msclkid)) {
						updatedAffiliateInfo.msclkid = updatedLead?.affiliateInfo?.msclkid;
						updatedSiteVisitFields.msclkid = updatedLead?.affiliateInfo?.msclkid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.twclid) && isStringNotEmpty(updatedLead?.affiliateInfo?.twclid)) {
						updatedAffiliateInfo.twclid = updatedLead?.affiliateInfo?.twclid;
						updatedSiteVisitFields.twclid = updatedLead?.affiliateInfo?.twclid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.nbclid) && isStringNotEmpty(updatedLead?.affiliateInfo?.nbclid)) {
						updatedAffiliateInfo.nbclid = updatedLead?.affiliateInfo?.nbclid;
						updatedSiteVisitFields.nbclid = updatedLead?.affiliateInfo?.nbclid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.gbraid) && isStringNotEmpty(updatedLead?.affiliateInfo?.gbraid)) {
						updatedAffiliateInfo.gbraid = updatedLead?.affiliateInfo?.gbraid;
						updatedSiteVisitFields.gbraid = updatedLead?.affiliateInfo?.gbraid;
					}
					if (isStringEmpty(updatedAffiliateInfo?.utm_source) && isStringNotEmpty(updatedLead?.affiliateInfo?.utm_source)) {
						updatedAffiliateInfo.utm_source = updatedLead?.affiliateInfo?.utm_source;
						updatedSiteVisitFields.utm_source = updatedLead?.affiliateInfo?.utm_source;
					}
					if (isStringEmpty(updatedAffiliateInfo?.utm_campaign) && isStringNotEmpty(updatedLead?.affiliateInfo?.utm_campaign)) {
						updatedAffiliateInfo.utm_campaign = updatedLead?.affiliateInfo?.utm_campaign;
						updatedSiteVisitFields.utm_campaign = updatedLead?.affiliateInfo?.utm_campaign;
					}
					if (isStringEmpty(updatedAffiliateInfo?.utm_content) && isStringNotEmpty(updatedLead?.affiliateInfo?.utm_content)) {
						updatedAffiliateInfo.utm_content = updatedLead?.affiliateInfo?.utm_content;
						updatedSiteVisitFields.utm_content = updatedLead?.affiliateInfo?.utm_content;
					}
					if (isStringEmpty(updatedAffiliateInfo?.utm_medium) && isStringNotEmpty(updatedLead?.affiliateInfo?.utm_medium)) {
						updatedAffiliateInfo.utm_medium = updatedLead?.affiliateInfo?.utm_medium;
						updatedSiteVisitFields.utm_medium = updatedLead?.affiliateInfo?.utm_medium;
					}
					if (isStringEmpty(updatedAffiliateInfo?.utm_term) && isStringNotEmpty(updatedLead?.affiliateInfo?.utm_term)) {
						updatedAffiliateInfo.utm_term = updatedLead?.affiliateInfo?.utm_term;
						updatedSiteVisitFields.utm_term = updatedLead?.affiliateInfo?.utm_term;
					}
					if (isStringEmpty(updatedAffiliateInfo?.productSku) && isStringNotEmpty(updatedLead?.affiliateInfo?.productSku)) {
						updatedAffiliateInfo.productSku = updatedLead?.affiliateInfo?.productSku;
					}
					if (isStringEmpty(updatedAffiliateInfo?.offerGroupId) && isStringNotEmpty(updatedLead?.affiliateInfo?.offerGroupId)) {
						updatedAffiliateInfo.offerGroupId = updatedLead?.affiliateInfo?.offerGroupId;
						updatedSiteVisitFields.offerGroupId = updatedLead?.affiliateInfo?.offerGroupId;
					}
					if (isStringEmpty(updatedAffiliateInfo?.offerToken) && isStringNotEmpty(updatedLead?.affiliateInfo?.offerToken)) {
						updatedAffiliateInfo.offerToken = updatedLead?.affiliateInfo?.offerToken;
						updatedSiteVisitFields.offerToken = updatedLead?.affiliateInfo?.offerToken;
					}

					updatedFields.affiliateInfo = updatedAffiliateInfo;
				}
			}

			if (isObjectNotEmpty(siteVisit) && isStringNotEmpty(siteVisit?.id)) {
				if (isStringNotEmpty(updatedFields.programId) && updatedFields.programId !== 'none' && (isStringEmpty(siteVisit?.programId) || (isStringNotEmpty(siteVisit?.programId) && siteVisit?.programId !== updatedFields.programId))) {
					updatedSiteVisitFields.programId = updatedFields.programId;
				}
				if (isStringNotEmpty(updatedFields.campaignId) && updatedFields.campaignId !== 'none' && (isStringEmpty(siteVisit?.campaignId) || (isStringNotEmpty(siteVisit?.campaignId) && siteVisit?.campaignId !== updatedFields.campaignId))) {
					updatedSiteVisitFields.campaignId = updatedFields.campaignId;
				}
				if (isStringNotEmpty(updatedFields.leadId) && (isStringEmpty(siteVisit?.leadId) || (isStringNotEmpty(siteVisit?.leadId) && siteVisit?.leadId !== updatedFields.leadId))) {
					updatedSiteVisitFields.leadId = updatedFields.leadId;
				}
				if (isStringNotEmpty(updatedFields.ipAddress) && (isStringEmpty(siteVisit?.ipAddress) || (isStringNotEmpty(siteVisit?.ipAddress) && siteVisit?.ipAddress !== updatedFields.ipAddress))) {
					updatedSiteVisitFields.ipAddress = updatedFields.ipAddress;
				}
				if (isStringNotEmpty(updatedFields.deviceFingerprint) && (isStringEmpty(siteVisit?.deviceFingerprint) || (isStringNotEmpty(siteVisit?.deviceFingerprint) && siteVisit?.deviceFingerprint !== updatedFields.deviceFingerprint))) {
					updatedSiteVisitFields.deviceFingerprint = updatedFields.deviceFingerprint;
				}
				if (isStringNotEmpty(updatedFields.couponCode) && (isStringEmpty(siteVisit?.couponCode) || (isStringNotEmpty(siteVisit?.couponCode) && siteVisit?.couponCode !== updatedFields.couponCode))) {
					updatedSiteVisitFields.couponCode = updatedFields.couponCode;
				}
				if (isNotNullOrUndefined(updatedFields.testMode) && (isNullOrUndefined(siteVisit?.testMode) || (isNotNullOrUndefined(siteVisit?.testMode) && siteVisit?.testMode !== updatedFields.testMode))) {
					updatedSiteVisitFields.testMode = updatedFields.testMode;
				}
				if (isStringNotEmpty(updatedFields.source) && isStringEmpty(siteVisit?.source)) {
					updatedSiteVisitFields.source = updatedFields.source;
				}
				if (isStringNotEmpty(updatedFields.version) && (isStringEmpty(siteVisit?.version) || (isStringNotEmpty(siteVisit?.version) && siteVisit?.version !== updatedFields.version))) {
					updatedSiteVisitFields.version = updatedFields.version;
				}
				if (isStringNotEmpty(updatedFields.offerGroupId) && (isStringEmpty(siteVisit?.offerGroupId) || siteVisit?.offerGroupId !== updatedFields.offerGroupId)) {
					updatedSiteVisitFields.offerGroupId = updatedFields.offerGroupId;
				}
				if (isStringNotEmpty(updatedFields.offerToken) && (isStringEmpty(siteVisit?.offerToken) || siteVisit?.offerToken !== updatedFields.offerToken)) {
					updatedSiteVisitFields.offerToken = updatedFields.offerToken;
				}

				if (isObjectNotEmptyWithoutEmptyParams(updatedSiteVisitFields)) {
					let updateSiteVisitSuccess = await SiteVisit.updateFields(siteVisit?.id, { ...updatedSiteVisitFields });
					if (isBooleanTrue(updateSiteVisitSuccess)) {
						updatedFields.siteVisit = { ...siteVisit, ...updatedSiteVisitFields };
					}
				}
			}

			if (isObjectNotEmpty(lead) && isStringNotEmpty(lead?.id)) {
				// console.log('...overwriting lead');
				// tracking replays the querystring coupon on every url change - only seed a missing coupon here; once set, only a checkout attempt may change it
				if (isStringNotEmpty(updatedFields.couponCode) && isStringEmpty(lead?.couponCode)) {
					updatedLeadFields.couponCode = updatedFields.couponCode;
					logger.logDebugEvent('ANALYTICS_LEAD_COUPON', `Setting lead couponCode to ${updatedFields.couponCode}`, getTestModeConsole());
				}
				if (isNotNullOrUndefined(updatedFields.testMode) && (isNullOrUndefined(lead?.testMode) || (isNotNullOrUndefined(lead?.testMode) && lead?.testMode !== updatedFields.testMode))) {
					updatedLeadFields.testMode = updatedFields.testMode;
				}
				if (isStringNotEmpty(updatedFields.offerGroupId) && (isStringEmpty(lead?.offerGroupId) || lead?.offerGroupId !== updatedFields.offerGroupId)) {
					updatedLeadFields.offerGroupId = updatedFields.offerGroupId;
				}
				if (isStringNotEmpty(updatedFields.offerToken) && (isStringEmpty(lead?.offerToken) || lead?.offerToken !== updatedFields.offerToken)) {
					updatedLeadFields.offerToken = updatedFields.offerToken;
				}
				// if (isStringNotEmpty(updatedFields.programId) && updatedFields.programId !== 'none' && (isStringEmpty(lead?.programId) || (isStringNotEmpty(lead?.programId) && lead?.programId !== updatedFields.programId))) {
				// 	updatedLeadFields.programId = updatedFields.programId;
				// }
				// if (isStringNotEmpty(updatedFields.campaignId) && updatedFields.campaignId !== 'none' && (isStringEmpty(lead?.campaignId) || (isStringNotEmpty(lead?.campaignId) && lead?.campaignId !== updatedFields.campaignId))) {
				// 	updatedLeadFields.campaignId = updatedFields.campaignId;
				// }

				if (isObjectNotEmptyWithoutEmptyParams(updatedLeadFields)) {
					let updateLeadSuccess = await Lead.updateFields(lead?.id, { ...updatedLeadFields });
					if (isBooleanTrue(updateLeadSuccess)) {
						updatedFields.lead = { ...lead, ...updatedLeadFields };
					}
				}
			}

			await dispatch({ type: 'UPDATE_ANALYTICS', payload: { ...updatedFields } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateAnalyticsFields = (updatedFields) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS', payload: { ...updatedFields } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateAnalyticsAndAffiliateInfo = (updatedFields) => {
	return async (dispatch, getState) => {
		try {
			updatedFields = isObjectNotEmpty(updatedFields) ? { ...updatedFields } : {};
			const state = getState();
			const analyticsState = { ...state.analyticsState };
			const affiliateInfo = isObjectNotEmpty(analyticsState.affiliateInfo) ? { ...analyticsState.affiliateInfo } : {};

			let siteVisit = analyticsState?.siteVisit;
			let lead = analyticsState?.lead;

			updatedFields = await resolveAnalyticsIdentityFields({ dispatch, analyticsState, updatedFields });
			if (updatedFields['productCategorySlug'] !== undefined) {
				const productCategories = await dispatch(getProductCategories(false));
				if (isListNotEmpty(productCategories)) {
					let productCategorySlug = updatedFields['productCategorySlug'];

					const productCategory = getProductCategoryBySlug(productCategorySlug, productCategories);
					const productCategoryId = productCategory?.id ?? 'none';

					updatedFields.productCategory = productCategory;
					updatedFields.productCategoryId = productCategoryId;
				}
			}
			if (updatedFields['campaignSlug'] !== undefined) {
				const programs = await dispatch(getPrograms(false));
				const campaigns = await dispatch(getCampaigns(false));
				if (isListNotEmpty(campaigns) && isListNotEmpty(programs)) {
					let campaignSlug = updatedFields['campaignSlug'];
					let programSlug = isStringNotEmpty(updatedFields['programSlug']) ? updatedFields['programSlug'] : analyticsState?.programSlug;

					const campaign = getDefaultCampaign(programSlug, campaignSlug, campaigns, programs);
					const campaignId = campaign?.id ?? 'none';

					updatedFields.campaign = campaign;
					updatedFields.campaignId = campaignId;
				}
			}

			if (isObjectNotEmpty(siteVisit) && isStringNotEmpty(siteVisit?.id)) {
				let updatedSiteVisitFields = {};
				if (isStringNotEmpty(updatedFields.programId) && updatedFields.programId !== 'none' && (isStringEmpty(siteVisit?.programId) || (isStringNotEmpty(siteVisit?.programId) && siteVisit?.programId !== updatedFields.programId))) {
					updatedSiteVisitFields.programId = updatedFields.programId;
				}
				if (isStringNotEmpty(updatedFields.campaignId) && updatedFields.campaignId !== 'none' && (isStringEmpty(siteVisit?.campaignId) || (isStringNotEmpty(siteVisit?.campaignId) && siteVisit?.campaignId !== updatedFields.campaignId))) {
					updatedSiteVisitFields.campaignId = updatedFields.campaignId;
				}
				if (isStringNotEmpty(updatedFields.leadId) && (isStringEmpty(siteVisit?.leadId) || (isStringNotEmpty(siteVisit?.leadId) && siteVisit?.leadId !== updatedFields.leadId))) {
					updatedSiteVisitFields.leadId = updatedFields.leadId;
				}
				if (isStringNotEmpty(updatedFields.userId) && (isStringEmpty(siteVisit?.userId) || (isStringNotEmpty(siteVisit?.userId) && siteVisit?.userId !== updatedFields.userId))) {
					updatedSiteVisitFields.userId = updatedFields.userId;
				}
				if (isStringNotEmpty(updatedFields.ipAddress) && (isStringEmpty(siteVisit?.ipAddress) || (isStringNotEmpty(siteVisit?.ipAddress) && siteVisit?.ipAddress !== updatedFields.ipAddress))) {
					updatedSiteVisitFields.ipAddress = updatedFields.ipAddress;
				}
				if (isStringNotEmpty(updatedFields.deviceFingerprint) && (isStringEmpty(siteVisit?.deviceFingerprint) || (isStringNotEmpty(siteVisit?.deviceFingerprint) && siteVisit?.deviceFingerprint !== updatedFields.deviceFingerprint))) {
					updatedSiteVisitFields.deviceFingerprint = updatedFields.deviceFingerprint;
				}
				if (isStringNotEmpty(updatedFields.couponCode) && (isStringEmpty(siteVisit?.couponCode) || (isStringNotEmpty(siteVisit?.couponCode) && siteVisit?.couponCode !== updatedFields.couponCode))) {
					updatedSiteVisitFields.couponCode = updatedFields.couponCode;
				}
				if (isNotNullOrUndefined(updatedFields.testMode) && (isNullOrUndefined(siteVisit?.testMode) || (isNotNullOrUndefined(siteVisit?.testMode) && siteVisit?.testMode !== updatedFields.testMode))) {
					updatedSiteVisitFields.testMode = updatedFields.testMode;
				}
				if (isStringNotEmpty(updatedFields.source) && isStringEmpty(siteVisit?.source)) {
					updatedSiteVisitFields.source = updatedFields.source;
				}
				if (isStringNotEmpty(updatedFields.version) && (isStringEmpty(siteVisit?.version) || (isStringNotEmpty(siteVisit?.version) && siteVisit?.version !== updatedFields.version))) {
					updatedSiteVisitFields.version = updatedFields.version;
				}
				if (isStringNotEmpty(updatedFields.offerGroupId) && (isStringEmpty(siteVisit?.offerGroupId) || siteVisit?.offerGroupId !== updatedFields.offerGroupId)) {
					updatedSiteVisitFields.offerGroupId = updatedFields.offerGroupId;
				}
				if (isStringNotEmpty(updatedFields.offerToken) && (isStringEmpty(siteVisit?.offerToken) || siteVisit?.offerToken !== updatedFields.offerToken)) {
					updatedSiteVisitFields.offerToken = updatedFields.offerToken;
				}

				if (isObjectNotEmptyWithoutEmptyParams(updatedSiteVisitFields)) {
					let updateSiteVisitSuccess = await SiteVisit.updateFields(siteVisit?.id, { ...updatedSiteVisitFields });
					if (isBooleanTrue(updateSiteVisitSuccess)) {
						updatedFields.siteVisit = { ...siteVisit, ...updatedSiteVisitFields };
					}
				}
			}

			if (isObjectNotEmpty(lead) && isStringNotEmpty(lead?.id)) {
				let updatedLeadFields = {};
				// tracking replays the querystring coupon on every url change - only seed a missing coupon here; once set, only a checkout attempt may change it
				if (isStringNotEmpty(updatedFields.couponCode) && isStringEmpty(lead?.couponCode)) {
					updatedLeadFields.couponCode = updatedFields.couponCode;
					logger.logDebugEvent('ANALYTICS_LEAD_COUPON', `Setting lead couponCode in updateAnalyticsAndAffiliateInfo to ${updatedFields.couponCode}`, getTestModeConsole());
				}
				if (isNotNullOrUndefined(updatedFields.testMode) && (isNullOrUndefined(lead?.testMode) || (isNotNullOrUndefined(lead?.testMode) && lead?.testMode !== updatedFields.testMode))) {
					updatedLeadFields.testMode = updatedFields.testMode;
				}
				if (isStringNotEmpty(updatedFields.offerGroupId) && (isStringEmpty(lead?.offerGroupId) || lead?.offerGroupId !== updatedFields.offerGroupId)) {
					updatedLeadFields.offerGroupId = updatedFields.offerGroupId;
				}
				if (isStringNotEmpty(updatedFields.offerToken) && (isStringEmpty(lead?.offerToken) || lead?.offerToken !== updatedFields.offerToken)) {
					updatedLeadFields.offerToken = updatedFields.offerToken;
				}
				// if (isStringNotEmpty(updatedFields.programId) && updatedFields.programId !== 'none' && (isStringEmpty(lead?.programId) || (isStringNotEmpty(lead?.programId) && lead?.programId !== updatedFields.programId))) {
				// 	updatedLeadFields.programId = updatedFields.programId;
				// }
				// if (isStringNotEmpty(updatedFields.campaignId) && updatedFields.campaignId !== 'none' && (isStringEmpty(lead?.campaignId) || (isStringNotEmpty(lead?.campaignId) && lead?.campaignId !== updatedFields.campaignId))) {
				// 	updatedLeadFields.campaignId = updatedFields.campaignId;
				// }

				if (isObjectNotEmptyWithoutEmptyParams(updatedLeadFields)) {
					let updateLeadSuccess = await Lead.updateFields(lead?.id, { ...updatedLeadFields });
					if (isBooleanTrue(updateLeadSuccess)) {
						updatedFields.lead = { ...lead, ...updatedLeadFields };
					}
				}
			}

			if (isObjectNotEmpty(affiliateInfo)) {
				let updatedAffiliateInfoFields = isObjectNotEmpty(updatedFields) ? { ...updatedFields } : {};
				if (isStringNotEmpty(updatedFields.programId) && updatedFields.programId !== 'none' && (isStringEmpty(affiliateInfo?.programId) || (isStringNotEmpty(affiliateInfo?.programId) && affiliateInfo?.programId !== updatedFields.programId))) {
					updatedAffiliateInfoFields.programId = updatedFields.programId;
				}
				if (isStringNotEmpty(updatedFields.campaignId) && updatedFields.campaignId !== 'none' && (isStringEmpty(affiliateInfo?.campaignId) || (isStringNotEmpty(affiliateInfo?.campaignId) && affiliateInfo?.campaignId !== updatedFields.campaignId))) {
					updatedAffiliateInfoFields.campaignId = updatedFields.campaignId;
				}
				if (isStringNotEmpty(updatedFields.leadId) && (isStringEmpty(affiliateInfo?.leadId) || (isStringNotEmpty(affiliateInfo?.leadId) && affiliateInfo?.leadId !== updatedFields.leadId))) {
					updatedAffiliateInfoFields.leadId = updatedFields.leadId;
				}
				if (isStringNotEmpty(updatedFields.ipAddress) && (isStringEmpty(affiliateInfo?.ipAddress) || (isStringNotEmpty(affiliateInfo?.ipAddress) && affiliateInfo?.ipAddress !== updatedFields.ipAddress))) {
					updatedAffiliateInfoFields.ipAddress = updatedFields.ipAddress;
				}
				if (isStringNotEmpty(updatedFields.deviceFingerprint) && (isStringEmpty(affiliateInfo?.deviceFingerprint) || (isStringNotEmpty(affiliateInfo?.deviceFingerprint) && affiliateInfo?.deviceFingerprint !== updatedFields.deviceFingerprint))) {
					updatedAffiliateInfoFields.deviceFingerprint = updatedFields.deviceFingerprint;
				}
				if (isStringNotEmpty(updatedFields.couponCode) && (isStringEmpty(affiliateInfo?.couponCode) || (isStringNotEmpty(affiliateInfo?.couponCode) && affiliateInfo?.couponCode !== updatedFields.couponCode))) {
					updatedAffiliateInfoFields.couponCode = updatedFields.couponCode;
				}
				if (isNotNullOrUndefined(updatedFields.testMode) && (isNullOrUndefined(affiliateInfo?.testMode) || (isNotNullOrUndefined(affiliateInfo?.testMode) && affiliateInfo?.testMode !== updatedFields.testMode))) {
					updatedAffiliateInfoFields.testMode = updatedFields.testMode;
				}
				if (isStringNotEmpty(updatedFields.source) && isStringEmpty(affiliateInfo?.source)) {
					updatedAffiliateInfoFields.source = updatedFields.source;
				}
				if (isStringNotEmpty(updatedFields.version) && (isStringEmpty(affiliateInfo?.version) || (isStringNotEmpty(affiliateInfo?.version) && affiliateInfo?.version !== updatedFields.version))) {
					updatedAffiliateInfoFields.version = updatedFields.version;
				}
				if (isStringNotEmpty(updatedFields.offerGroupId) && (isStringEmpty(affiliateInfo?.offerGroupId) || affiliateInfo?.offerGroupId !== updatedFields.offerGroupId)) {
					updatedAffiliateInfoFields.offerGroupId = updatedFields.offerGroupId;
				}
				if (isStringNotEmpty(updatedFields.offerToken) && (isStringEmpty(affiliateInfo?.offerToken) || affiliateInfo?.offerToken !== updatedFields.offerToken)) {
					updatedAffiliateInfoFields.offerToken = updatedFields.offerToken;
				}

				if (isObjectNotEmptyWithoutEmptyParams(updatedAffiliateInfoFields)) {
					updatedFields.affiliateInfo = new AffiliateInfo({ ...affiliateInfo, ...updatedAffiliateInfoFields }).toDto();
				}
			}

			await dispatch({ type: 'UPDATE_ANALYTICS', payload: { ...updatedFields } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateAnalyticsAndLead = (updatedFields) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const analyticsState = { ...state.analyticsState };
			const { lead } = analyticsState;

			let updatedAnalyticsFields = {
				...updatedFields
			};

			if (isObjectNotEmpty(lead) && isStringNotEmpty(lead?.id)) {
				let success = await Lead.updateFields(lead?.id, { ...updatedFields });
				if (isBooleanTrue(success)) {
					updatedAnalyticsFields.lead = { ...lead, ...updatedFields };
				}
			}

			await dispatch({ type: 'UPDATE_ANALYTICS', payload: { ...updatedAnalyticsFields } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateLead = (id, updatedFields) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const analyticsState = { ...state.analyticsState };
			const { lead } = analyticsState;

			if (isObjectNotEmpty(lead) && isStringNotEmpty(lead?.id) && isStringNotEmpty(id) && lead?.id === id) {
				// redux first. the UI reads this record from here, and holding the update back until
				// Firestore acknowledged is what made every funnel step wait on a round trip - 236 call
				// sites across the three thunks, 215 of them awaited, and not one reads a return value, so
				// nothing was ever waiting for the write, only for state the customer could already be shown
				await dispatch({ type: 'UPDATE_ANALYTICS_LEAD', payload: { ...lead, ...updatedFields } });

				// deliberately not awaited, and deliberately not returned: returning the promise would put
				// every existing `await dispatch(...)` straight back onto the round trip. a failure is
				// logged rather than surfaced. a field the funnel writes on every step recovers on the next
				// one; a field written once does not
				saveFunnelProgress({ leadId: id, leadFields: { ...updatedFields } }).catch((error) => {
					logger.logReduxErrorEvent(error, getErrorMessage(error), true);
				});
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateSiteVisit = (siteVisitId, updatedFields) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const analyticsState = { ...state.analyticsState };
			const { siteVisit } = analyticsState;

			if (isObjectNotEmpty(siteVisit) && isStringNotEmpty(siteVisit?.id) && isStringNotEmpty(siteVisitId) && siteVisit?.id === siteVisitId) {
				// redux first. the UI reads this record from here, and holding the update back until
				// Firestore acknowledged is what made every funnel step wait on a round trip - 236 call
				// sites across the three thunks, 215 of them awaited, and not one reads a return value, so
				// nothing was ever waiting for the write, only for state the customer could already be shown
				await dispatch({ type: 'UPDATE_ANALYTICS_SITEVISIT', payload: { ...siteVisit, ...updatedFields } });

				// deliberately not awaited, and deliberately not returned: returning the promise would put
				// every existing `await dispatch(...)` straight back onto the round trip. a failure is
				// logged rather than surfaced. a field the funnel writes on every step recovers on the next
				// one; a field written once does not
				saveFunnelProgress({ siteVisitId: siteVisitId, siteVisitFields: { ...updatedFields } }).catch((error) => {
					logger.logReduxErrorEvent(error, getErrorMessage(error), true);
				});
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const fireButtonClickEvent = (buttonId) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const analyticsState = { ...state.analyticsState };
			const { siteVisit } = analyticsState;

			if (isStringNotEmpty(buttonId) && isObjectNotEmpty(siteVisit) && isStringNotEmpty(siteVisit?.id)) {
				await new SiteEvent({
					...siteVisit,

					funnel: 'Button Click',
					stepNumber: 0,
					stepName: buttonId,
					direction: 'Clicked',

					eventName: 'buttonclick',
					pathName: window?.location?.pathname,

					url: window?.location?.href,

					siteVisitId: siteVisit?.id
				}).create();
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const getReferrerUrlAction = () => {
	return async (dispatch) => {
		try {
			let referrerUrl = getReferrerUrl();

			await dispatch({ type: 'UPDATE_ANALYTICS', payload: { referrerUrl: referrerUrl } });

			return referrerUrl;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return null;
	}
};

export const getUserAgentAction = () => {
	return async (dispatch) => {
		try {
			let userAgent = getClientUserAgent();

			await dispatch({ type: 'UPDATE_ANALYTICS', payload: { userAgent: userAgent } });

			return userAgent;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return null;
	}
};

export const getEmailInfo = (email) => {
	return async (dispatch) => {
		try {
			let emailInfo = await validateEmail(email);
			// console.log('emailInfo', emailInfo);

			if (isObjectNotEmpty(emailInfo)) {
				await dispatch(updateAnalyticsAndLead({ emailInfo: emailInfo }));

				return emailInfo;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return null;
	}
};

export const getPhoneInfo = (phone) => {
	return async (dispatch) => {
		try {
			let phoneInfo = await validatePhone(phone);
			// console.log('phoneInfo', phoneInfo);

			if (isObjectNotEmpty(phoneInfo)) {
				await dispatch(updateAnalyticsAndLead({ phoneInfo: phoneInfo }));

				return phoneInfo;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return null;
	}
};

export const getIPAddress = () => {
	return async (dispatch) => {
		try {
			// IP Address Fetch
			let ipAddress = await getClientIPAddress();
			// console.log('ipAddress', ipAddress);

			// a failed lookup must not dispatch an explicit null - the blind UPDATE_ANALYTICS
			// spread would wipe a server-stamped ip that tracking initialization already set
			if (isStringNotEmpty(ipAddress)) {
				await dispatch(updateAnalyticsAndAffiliateInfo({ ipAddress: ipAddress }));
			}

			return ipAddress;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return null;
	}
};

export const getDeviceFingerprint = () => {
	return async (dispatch) => {
		try {
			// IPQS Device Fingerprinting
			await DeviceFingerprint.initializeScriptAsync(import.meta.env.VITE_QUALITY_SCORE_FINGERPRINT_TRACKER_KEY);

			DeviceFingerprint.AfterResult(function (result) {
				// console.log('deviceInfo', result);

				if (isObjectNotEmpty(result)) {
					dispatch(updateAnalyticsAndAffiliateInfo({ deviceFingerprint: result?.device_id }))
					dispatch({ type: 'UPDATE_ANALYTICS', payload: { deviceInfo: result } });
					// console.log(result.device_id);
					// console.log(result.ip_address);

					// redirect or perform business logic if Fraud Score is >= recommended value of 90
					if (result?.fraud_chance >= 90) {
						// window.location.href = "";	
					}
				} else {
					dispatch(updateAnalyticsAndAffiliateInfo({ deviceFingerprint: null }));
					dispatch({ type: 'UPDATE_ANALYTICS', payload: { deviceInfo: { failureReason: 'IPQS returned an empty result' } } });
				}
			});

			DeviceFingerprint.AfterFailure(function () {
				dispatch(updateAnalyticsAndAffiliateInfo({ deviceFingerprint: null }));
				dispatch({ type: 'UPDATE_ANALYTICS', payload: { deviceInfo: { failureReason: 'Failed' } } });
			});

			DeviceFingerprint.Init();
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			dispatch(updateAnalyticsAndAffiliateInfo({ deviceFingerprint: null }));
			dispatch({ type: 'UPDATE_ANALYTICS', payload: { deviceInfo: { failureReason: error.message } } });
		}
	}
};

export const resetAnalytics = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'RESET_ANALYTICS', payload: { ...initialState } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setLead = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_LEAD', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setLeadId = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_LEADID', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSiteVisit = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_SITEVISIT', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSiteVisitId = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_SITEVISITID', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSiteVisitAt = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_SITEVISITAT', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setFbc = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_FBC', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setDeviceFingerprint = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_DEVICEFINGERPRINT', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIPAddress = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_IPADDRESS', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setDeviceInfo = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_DEVICEINFO', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setEmailInfo = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_EMAILINFO', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setPhoneInfo = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_PHONEINFO', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setAffiliateInfo = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_AFFILIATEINFO', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setInitialPath = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_INITIALPATH', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setTheOfferSession = (val) => {
	return async (dispatch) => {
		try {
			await dispatch({ type: 'UPDATE_ANALYTICS_THEOFFERSESSION', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};


export const useAnalytics = () => {
	const dispatch = useDispatch();

	const programSlug = useSelector((appState) => appState.analyticsState.programSlug);
	const productSku = useSelector((appState) => appState.analyticsState.productSku);
	const offerGroupId = useSelector((appState) => appState.analyticsState.offerGroupId);
	const offerToken = useSelector((appState) => appState.analyticsState.offerToken);
	const productCategorySlug = useSelector((appState) => appState.analyticsState.productCategorySlug);
	const campaignSlug = useSelector((appState) => appState.analyticsState.campaignSlug);

	//#region useSelectors

	const program = useSelector((appState) => selectProgramBySlug(appState, programSlug));
	const campaign = useSelector((appState) => selectDefaultCampaignByProgramSlug(appState, programSlug, campaignSlug));
	const product = useSelector((appState) => selectDefaultProductByProgramSlug(appState, programSlug, productSku));
	const productCategory = useSelector((appState) => selectProductCategoryBySlug(appState, productCategorySlug));

	//#endregion
	//#region useMemos 

	const programId = useMemo(() => {
		if (isObjectNotEmpty(program) && isStringNotEmpty(program?.id)) {
			return program?.id;
		}

		return 'none';
	}, [program?.id]);

	const campaignId = useMemo(() => {
		if (isObjectNotEmpty(campaign) && isStringNotEmpty(campaign?.id)) {
			return campaign?.id;
		}

		return 'none';
	}, [campaign?.id]);

	const productId = useMemo(() => {
		if (isObjectNotEmpty(product) && isStringNotEmpty(product?.id)) {
			return product?.id;
		}

		return null;
	}, [product?.id]);

	const productCategoryId = useMemo(() => {
		if (isObjectNotEmpty(productCategory) && isStringNotEmpty(productCategory?.id)) {
			return productCategory?.id;
		}

		return 'none';
	}, [productCategory?.id]);

	//#endregion

	// console.log('programSlug', programSlug, programId);

	return {
		analyticsContext: useSelector((appState) => appState.analyticsState),

		siteVisit: useSelector((appState) => appState.analyticsState.siteVisit),
		siteVisitId: useSelector((appState) => appState.analyticsState.siteVisitId),
		siteVisitAt: useSelector((appState) => appState.analyticsState.siteVisitAt),

		deviceInfo: useSelector((appState) => appState.analyticsState.deviceInfo),
		emailInfo: useSelector((appState) => appState.analyticsState.emailInfo),
		phoneInfo: useSelector((appState) => appState.analyticsState.phoneInfo),
		affiliateInfo: useSelector((appState) => appState.analyticsState.affiliateInfo),
		ipAddress: useSelector((appState) => appState.analyticsState.ipAddress),
		referrerUrl: useSelector((appState) => appState.analyticsState.referrerUrl),
		userAgent: useSelector((appState) => appState.analyticsState.userAgent),
		deviceFingerprint: useSelector((appState) => appState.analyticsState.deviceFingerprint),

		affiliateId: useSelector((appState) => appState.analyticsState.affiliateId),
		subAffiliateId: useSelector((appState) => appState.analyticsState.subAffiliateId),
		creativeId: useSelector((appState) => appState.analyticsState.creativeId),
		version: useSelector((appState) => appState.analyticsState.version),
		clickId: useSelector((appState) => appState.analyticsState.clickId),
		source: useSelector((appState) => appState.analyticsState.source),
		couponCode: useSelector((appState) => appState.analyticsState.couponCode),

		testMode: useSelector((appState) => appState.analyticsState.testMode),

		// campaignSlug: useSelector((appState) => appState.analyticsState.campaignSlug),
		// campaignId: useSelector((appState) => appState.analyticsState.campaignId),
		// campaign: useSelector((appState) => appState.analyticsState.campaign),

		// programSlug: useSelector((appState) => appState.analyticsState.programSlug),
		// programId: useSelector((appState) => appState.analyticsState.programId),
		// program: useSelector((appState) => appState.analyticsState.program),

		fbclid: useSelector((appState) => appState.analyticsState.fbclid),
		fbc: useSelector((appState) => appState.analyticsState.fbc),
		ttclid: useSelector((appState) => appState.analyticsState.ttclid),
		gclid: useSelector((appState) => appState.analyticsState.gclid),
		irclickid: useSelector((appState) => appState.analyticsState.irclickid),
		rdt_cid: useSelector((appState) => appState.analyticsState.rdt_cid),
		SsCid: useSelector((appState) => appState.analyticsState.SsCid),
		msclkid: useSelector((appState) => appState.analyticsState.msclkid),
		twclid: useSelector((appState) => appState.analyticsState.twclid),
		nbclid: useSelector((appState) => appState.analyticsState.nbclid),
		gbraid: useSelector((appState) => appState.analyticsState.gbraid),

		addon: useSelector((appState) => appState.analyticsState.addon),

		// productSku: useSelector((appState) => appState.analyticsState.productSku),
		// product: useSelector((appState) => appState.analyticsState.product),
		// productId: useSelector((appState) => appState.analyticsState.productId),

		// productCategorySlug: useSelector((appState) => appState.analyticsState.productCategorySlug),
		// productCategory: useSelector((appState) => appState.analyticsState.productCategory),
		// productCategoryId: useSelector((appState) => appState.analyticsState.productCategoryId),

		campaignSlug: campaignSlug,
		campaignId: campaignId,
		campaign: campaign,

		programSlug: programSlug,
		programId: programId,
		program: program,

		productSku: productSku,
		product: product,
		productId: productId,
		offerGroupId: offerGroupId,
		offerToken: offerToken,

		productCategorySlug: productCategorySlug,
		productCategory: productCategory,
		productCategoryId: productCategoryId,

		offerId: useSelector((appState) => appState.analyticsState.offerId),
		affId: useSelector((appState) => appState.analyticsState.affId),
		sub1: useSelector((appState) => appState.analyticsState.sub1),
		sub2: useSelector((appState) => appState.analyticsState.sub2),
		sub3: useSelector((appState) => appState.analyticsState.sub3),
		sub4: useSelector((appState) => appState.analyticsState.sub4),
		sub5: useSelector((appState) => appState.analyticsState.sub5),
		uId: useSelector((appState) => appState.analyticsState.uId),
		sourceId: useSelector((appState) => appState.analyticsState.sourceId),
		transactionId: useSelector((appState) => appState.analyticsState.transactionId),
		sessionId: useSelector((appState) => appState.analyticsState.sessionId),
		theOfferSession: useSelector((appState) => appState.analyticsState.theOfferSession),

		utm_source: useSelector((appState) => appState.analyticsState.utm_source),
		utm_campaign: useSelector((appState) => appState.analyticsState.utm_campaign),
		utm_content: useSelector((appState) => appState.analyticsState.utm_content),
		utm_medium: useSelector((appState) => appState.analyticsState.utm_medium),
		utm_term: useSelector((appState) => appState.analyticsState.utm_term),

		leadId: useSelector((appState) => appState.analyticsState.leadId),
		lead: useSelector((appState) => appState.analyticsState.lead),

		isAnalyticsLoading: useSelector((appState) => appState.analyticsState.isLoading),
		analyticsError: useSelector((appState) => appState.analyticsState.error),

		getSiteVisit: (siteVisitId) => dispatch(getSiteVisit(siteVisitId)),
		updateAnalytics: (updatedFields) => dispatch(updateAnalytics(updatedFields)),
		setAnalytics: (updatedFields) => dispatch(updateAnalytics(updatedFields)),
		updateAnalyticsAndAffiliateInfo: (updatedFields) => dispatch(updateAnalyticsAndAffiliateInfo(updatedFields)),
		updateAnalyticsAndLead: (updatedFields) => dispatch(updateAnalyticsAndLead(updatedFields)),
		updateSiteVisit: (siteVisitId, updatedFields) => dispatch(updateSiteVisit(siteVisitId, updatedFields)),

		setLead: (val) => dispatch(updateAnalytics({ lead: val })),
		setLeadId: (val) => dispatch(updateAnalytics({ leadId: val })),

		setSiteVisit: (val) => dispatch(updateAnalytics({ siteVisit: val })),
		setSiteVisitId: (val) => dispatch(updateAnalytics({ siteVisitId: val })),
		setSiteVisitAt: (val) => dispatch(updateAnalytics({ siteVisitAt: val })),

		setFbc: (val) => dispatch(updateAnalytics({ fbc: val })),

		setDeviceFingerprint: (val) => dispatch(updateAnalytics({ deviceFingerprint: val })),
		setIPAddress: (val) => dispatch(updateAnalytics({ ipAddress: val })),
		setDeviceInfo: (val) => dispatch(updateAnalytics({ deviceInfo: val })),
		setEmailInfo: (val) => dispatch(updateAnalytics({ emailInfo: val })),
		setPhoneInfo: (val) => dispatch(updateAnalytics({ phoneInfo: val })),
		setAffiliateInfo: (val) => dispatch(updateAnalytics({ affiliateInfo: val })),
		setInitialPath: (val) => dispatch(updateAnalytics({ initialPath: val })),
		setTheOfferSession: (theOfferSession) => dispatch(setTheOfferSession(theOfferSession)),

		getReferrerUrl: () => dispatch(getReferrerUrlAction()),
		getUserAgent: () => dispatch(getUserAgentAction()),
		getEmailInfo: (email) => dispatch(getEmailInfo(email)),
		getPhoneInfo: (phone) => dispatch(getPhoneInfo(phone)),
		getIPAddress: () => dispatch(getIPAddress()),
		getDeviceFingerprint: () => dispatch(getDeviceFingerprint()),
		fireButtonClickEvent: (buttonId) => dispatch(fireButtonClickEvent(buttonId)),
		resetAnalytics: () => dispatch(resetAnalytics())
	};
};

export default analyticsReducer;
